import type {
  LoginRequest,
  MeResponse,
  PermissionGrant,
  TenantInfo,
  TokenPairResponse,
} from '@/core/types/auth';
import type {
  AssignLeadRequest,
  CreateLeadRequest,
  DuplicateCheckRequest,
  DuplicateCheckResponse,
  Lead,
  LeadListQuery,
  LeadWithEtag,
  PagedResponse,
  UpdateLeadRequest,
  QualifyLeadRequest,
  DisqualifyLeadRequest,
  ConvertLeadRequest,
  LeadConversionResponse,
  LeadStatusHistory,
} from '@/core/types/lead';
import { ApiError } from '@/core/utils/errors';
import { createUuid } from '@/core/utils/uuid';
import { mockDatabase } from './mockDatabase';
import { moduleMockStore } from './moduleMockStore';
import {
  demoTeam,
  demoTenant,
  demoUsers,
  employeePermissions,
  managerPermissions,
} from './mockSeed';

export type DemoRole = keyof typeof demoUsers;

const delay = async (milliseconds = 220): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const normalizePhone = (value: string | null | undefined): string =>
  (value || '').replace(/[\s()-]/g, '').replace(/^0098/, '+98').replace(/^0(?=9)/, '+98');

const normalizeEmail = (value: string | null | undefined): string => (value || '').trim().toLowerCase();

const parseRole = (token: string | null | undefined): DemoRole => {
  const role = token?.split('.')[1];
  if (role === 'manager' || role === 'employee') return role;
  throw new ApiError('نشست نمایشی معتبر نیست.', 401);
};

const getUser = (token: string): (typeof demoUsers)[DemoRole] => demoUsers[parseRole(token)];

const getPermissions = (role: DemoRole): PermissionGrant[] =>
  role === 'manager' ? managerPermissions : employeePermissions;

const ensureVisible = (lead: Lead, token: string): void => {
  const user = getUser(token);
  if (user.role === 'employee' && lead.ownerMembershipId !== user.membershipId) {
    throw new ApiError('به این سرنخ دسترسی ندارید.', 404);
  }
};

const createTokenPair = (role: DemoRole): TokenPairResponse => {
  const expiresAt = new Date(Date.now() + 60 * 60 * 1000);
  return {
    accessToken: `demo.${role}.${createUuid()}`,
    refreshToken: `demo-refresh.${role}.${createUuid()}`,
    expiresIn: 3600,
    expiresAtUtc: expiresAt.toISOString(),
  };
};

export const mockAuthApi = {
  async login(request: LoginRequest): Promise<TokenPairResponse> {
    await delay();
    const user = Object.values(demoUsers).find(
      (candidate) =>
        candidate.username.toLowerCase() === request.username.trim().toLowerCase() &&
        candidate.password === request.password &&
        request.tenantCode.trim().toLowerCase() === demoTenant.code,
    );

    if (!user) throw new ApiError('کد سازمان، نام کاربری یا رمز عبور نادرست است.', 401);
    return createTokenPair(user.role);
  },

  async refresh(refreshToken: string): Promise<TokenPairResponse> {
    await delay(100);
    const role = refreshToken.split('.')[1];
    if (role !== 'manager' && role !== 'employee') {
      throw new ApiError('Refresh Token معتبر نیست.', 401);
    }
    return createTokenPair(role);
  },

  async logout(): Promise<void> {
    await delay(80);
  },

  async getMe(accessToken: string): Promise<MeResponse> {
    await delay(120);
    const user = getUser(accessToken);
    return {
      userId: user.userId,
      tenantId: demoTenant.id,
      membership: {
        id: user.membershipId,
        tenantId: demoTenant.id,
        userId: user.userId,
        branchId: null,
        displayName: user.displayName,
        status: 'active',
      },
    };
  },

  async getPermissions(accessToken: string): Promise<PermissionGrant[]> {
    await delay(120);
    return getPermissions(parseRole(accessToken));
  },

  async getTenant(accessToken: string): Promise<TenantInfo> {
    await delay(120);
    parseRole(accessToken);
    return demoTenant;
  },
};

export const mockLeadsApi = {
  async list(accessToken: string, query: LeadListQuery = {}): Promise<PagedResponse<Lead>> {
    await delay();
    const user = getUser(accessToken);
    let leads = mockDatabase.getLeads();

    if (user.role === 'employee') {
      leads = leads.filter((lead) => lead.ownerMembershipId === user.membershipId);
    }
    if (query.ownerMembershipId) {
      leads = leads.filter((lead) => lead.ownerMembershipId === query.ownerMembershipId);
    }
    if (query.status) leads = leads.filter((lead) => lead.status === query.status);
    if (query.priority) leads = leads.filter((lead) => lead.priority === query.priority);
    if (query.unassigned !== undefined) {
      leads = leads.filter((lead) => query.unassigned ? !lead.ownerMembershipId : Boolean(lead.ownerMembershipId));
    }
    if (query.source?.trim()) leads = leads.filter((lead) => lead.source === query.source?.trim());
    if (query.minimumScore !== undefined) leads = leads.filter((lead) => lead.score >= query.minimumScore!);
    if (query.createdFromUtc) leads = leads.filter((lead) => lead.createdAtUtc >= query.createdFromUtc!);
    if (query.createdToUtc) leads = leads.filter((lead) => lead.createdAtUtc <= query.createdToUtc!);
    if (query.search?.trim()) {
      const search = query.search.trim().toLowerCase();
      leads = leads.filter((lead) =>
        [lead.firstName, lead.lastName, lead.companyName, lead.mobile, lead.email, lead.chiefExecutiveName, lead.chiefExecutivePhone, lead.industry, lead.contactPersonName, lead.contactPersonPhone]
          .filter(Boolean)
          .some((value) => value?.toLowerCase().includes(search)),
      );
    }

    const direction = query.sortDirection === 'asc' ? 1 : -1;
    leads.sort((left, right) => {
      if (query.sortBy === 'score') return (left.score - right.score) * direction;
      if (query.sortBy === 'priority') return left.priority.localeCompare(right.priority) * direction;
      if (query.sortBy === 'name') return (left.lastName || left.firstName || '').localeCompare(right.lastName || right.firstName || '') * direction;
      if (query.sortBy === 'updatedAtUtc') return (left.updatedAtUtc || '').localeCompare(right.updatedAtUtc || '') * direction;
      return left.createdAtUtc.localeCompare(right.createdAtUtc) * direction;
    });
    const page = Math.max(1, query.page || 1);
    const pageSize = Math.min(100, Math.max(1, query.pageSize || 25));
    const start = (page - 1) * pageSize;

    return {
      page,
      pageSize,
      totalCount: leads.length,
      items: leads.slice(start, start + pageSize),
    };
  },

  async get(accessToken: string, leadId: string): Promise<LeadWithEtag> {
    await delay();
    const lead = mockDatabase.getLeads().find((item) => item.id === leadId);
    if (!lead) throw new ApiError('سرنخ پیدا نشد.', 404);
    ensureVisible(lead, accessToken);
    return { lead, etag: `\"${lead.rowVersion}\"` };
  },

  async duplicateCheck(
    accessToken: string,
    request: DuplicateCheckRequest,
  ): Promise<DuplicateCheckResponse> {
    await delay(160);
    const user = getUser(accessToken);
    const mobile = normalizePhone(request.mobile);
    const email = normalizeEmail(request.email);
    const chiefExecutivePhone = normalizePhone(request.chiefExecutivePhone);
    if (!mobile && !email && !chiefExecutivePhone) throw new ApiError('شماره تماس یا ایمیل را وارد کنید.', 400);

    let leads = mockDatabase.getLeads();
    if (user.role === 'employee') {
      leads = leads.filter((lead) => lead.ownerMembershipId === user.membershipId);
    }

    const candidates = leads
      .map((lead) => {
        const matchedFields: string[] = [];
        if (mobile && [normalizePhone(lead.mobile), normalizePhone(lead.chiefExecutivePhone)].includes(mobile)) matchedFields.push('mobile');
        if (chiefExecutivePhone && [normalizePhone(lead.chiefExecutivePhone), normalizePhone(lead.mobile)].includes(chiefExecutivePhone)) matchedFields.push('chiefExecutivePhone');
        if (email && normalizeEmail(lead.email) === email) matchedFields.push('email');
        return {
          leadId: lead.id,
          matchScore: matchedFields.length === 2 ? 100 : matchedFields.length === 1 ? 90 : 0,
          matchedFields,
        };
      })
      .filter((item) => item.matchScore > 0)
      .slice(0, 5);

    return { hasPossibleDuplicates: candidates.length > 0, candidates };
  },

  async create(
    accessToken: string,
    request: CreateLeadRequest,
    _idempotencyKey: string,
  ): Promise<LeadWithEtag> {
    await delay();
    const user = getUser(accessToken);
    if (!request.mobile && !request.email && !request.chiefExecutivePhone && !request.contactPersonPhone) {
      throw new ApiError('حداقل یک شماره تماس یا ایمیل الزامی است.', 400);
    }

    const duplicateCheck = request.mobile || request.email || request.chiefExecutivePhone
      ? await this.duplicateCheck(accessToken, { mobile: request.mobile, email: request.email, chiefExecutivePhone: request.chiefExecutivePhone })
      : { hasPossibleDuplicates: false, candidates: [] };
    const rowVersion = mockDatabase.nextRowVersion();
    const lead: Lead = {
      id: createUuid(),
      firstName: request.firstName?.trim() || null,
      lastName: request.lastName?.trim() || null,
      companyName: request.companyName?.trim() || null,
      mobile: request.mobile?.trim() || null,
      email: request.email?.trim() || null,
      status: 'new',
      priority: request.priority,
      score:
        25 +
        (request.priority === 'urgent' ? 35 : request.priority === 'high' ? 20 : 0) +
        (request.companyName ? 10 : 0) +
        (request.email ? 5 : 0),
      source: request.source?.trim() || null,
      ownerMembershipId: user.role === 'employee' ? user.membershipId : null,
      ownerTeamId: request.ownerTeamId || (user.role === 'employee' ? demoTeam.id : null),
      branchId: null,
      description: request.description?.trim() || null,
      chiefExecutiveName: request.chiefExecutiveName?.trim() || null,
      chiefExecutivePhone: request.chiefExecutivePhone?.trim() || null,
      industry: request.industry?.trim() || null,
      contactPersonName: request.contactPersonName?.trim() || null,
      contactPersonPhone: request.contactPersonPhone?.trim() || null,
      createdAtUtc: new Date().toISOString(),
      updatedAtUtc: null,
      rowVersion,
      duplicateCheck,
    };

    mockDatabase.saveLeads([lead, ...mockDatabase.getLeads()]);
    return { lead, etag: `\"${rowVersion}\"` };
  },

  async update(
    accessToken: string,
    leadId: string,
    request: UpdateLeadRequest,
    etag: string,
  ): Promise<LeadWithEtag> {
    await delay();
    const leads = mockDatabase.getLeads();
    const index = leads.findIndex((item) => item.id === leadId);
    if (index < 0) throw new ApiError('سرنخ پیدا نشد.', 404);
    const current = leads[index];
    ensureVisible(current, accessToken);
    if (!etag.includes(current.rowVersion)) {
      throw new ApiError('این اطلاعات توسط کاربر دیگری تغییر کرده است.', 412);
    }

    const rowVersion = mockDatabase.nextRowVersion();
    const updated: Lead = {
      ...current,
      firstName: request.firstName === undefined ? current.firstName : request.firstName?.trim() || null,
      lastName: request.lastName === undefined ? current.lastName : request.lastName?.trim() || null,
      companyName:
        request.companyName === undefined ? current.companyName : request.companyName?.trim() || null,
      mobile: request.mobile === undefined ? current.mobile : request.mobile?.trim() || null,
      email: request.email === undefined ? current.email : request.email?.trim() || null,
      priority: request.priority || current.priority,
      description:
        request.description === undefined ? current.description : request.description?.trim() || null,
      chiefExecutiveName: request.chiefExecutiveName === undefined ? current.chiefExecutiveName : request.chiefExecutiveName?.trim() || null,
      chiefExecutivePhone: request.chiefExecutivePhone === undefined ? current.chiefExecutivePhone : request.chiefExecutivePhone?.trim() || null,
      industry: request.industry === undefined ? current.industry : request.industry?.trim() || null,
      contactPersonName: request.contactPersonName === undefined ? current.contactPersonName : request.contactPersonName?.trim() || null,
      contactPersonPhone: request.contactPersonPhone === undefined ? current.contactPersonPhone : request.contactPersonPhone?.trim() || null,
      updatedAtUtc: new Date().toISOString(),
      rowVersion,
    };
    leads[index] = updated;
    mockDatabase.saveLeads(leads);
    return { lead: updated, etag: `\"${rowVersion}\"` };
  },

  async assign(
    accessToken: string,
    leadId: string,
    request: AssignLeadRequest,
    etag: string,
  ): Promise<LeadWithEtag> {
    await delay();
    if (parseRole(accessToken) !== 'manager') throw new ApiError('مجوز تخصیص ندارید.', 403);
    const leads = mockDatabase.getLeads();
    const index = leads.findIndex((item) => item.id === leadId);
    if (index < 0) throw new ApiError('سرنخ پیدا نشد.', 404);
    const current = leads[index];
    if (!etag.includes(current.rowVersion)) {
      throw new ApiError('این اطلاعات توسط کاربر دیگری تغییر کرده است.', 412);
    }

    const rowVersion = mockDatabase.nextRowVersion();
    const updated: Lead = {
      ...current,
      ownerMembershipId: request.membershipId,
      ownerTeamId: request.teamId || null,
      updatedAtUtc: new Date().toISOString(),
      rowVersion,
    };
    leads[index] = updated;
    mockDatabase.saveLeads(leads);
    return { lead: updated, etag: `\"${rowVersion}\"` };
  },

  async qualify(accessToken: string, leadId: string, request: QualifyLeadRequest): Promise<Lead> {
    return this.transition(accessToken, leadId, 'qualified', request.reason || null);
  },

  async disqualify(accessToken: string, leadId: string, request: DisqualifyLeadRequest): Promise<Lead> {
    if (!request.reason.trim()) throw new ApiError('دلیل رد الزامی است.', 400);
    return this.transition(accessToken, leadId, 'disqualified', request.reason);
  },

  async transition(accessToken: string, leadId: string, status: 'qualified' | 'disqualified', reason: string | null): Promise<Lead> {
    await delay(); const leads = mockDatabase.getLeads(); const index = leads.findIndex(x => x.id === leadId); if (index < 0) throw new ApiError('سرنخ پیدا نشد.', 404); const current = leads[index]; ensureVisible(current, accessToken); if (!['new', 'working'].includes(current.status)) throw new ApiError('تغییر وضعیت برای این سرنخ مجاز نیست.', 409);
    const updated = { ...current, status, updatedAtUtc: new Date().toISOString(), rowVersion: mockDatabase.nextRowVersion() }; leads[index] = updated; mockDatabase.saveLeads(leads);
    const state = moduleMockStore.get(); const history: LeadStatusHistory = { id: createUuid(), fromStatus: current.status, toStatus: status, actorMembershipId: getUser(accessToken).membershipId, reason, occurredAtUtc: new Date().toISOString() }; state.leadHistory[leadId] = [history, ...(state.leadHistory[leadId] || [])]; moduleMockStore.save(state); return updated;
  },

  async history(accessToken: string, leadId: string): Promise<LeadStatusHistory[]> { const lead = mockDatabase.getLeads().find(x => x.id === leadId); if (!lead) throw new ApiError('سرنخ پیدا نشد.', 404); ensureVisible(lead, accessToken); return moduleMockStore.get().leadHistory[leadId] || []; },

  async convert(accessToken: string, leadId: string, request: ConvertLeadRequest): Promise<LeadConversionResponse> {
    await delay();
    const leads = mockDatabase.getLeads();
    const index = leads.findIndex((item) => item.id === leadId);
    if (index < 0) throw new ApiError('سرنخ پیدا نشد.', 404);
    const lead = leads[index];
    ensureVisible(lead, accessToken);
    if (lead.status === 'converted') return { leadId, contactId: lead.convertedContactId || null, accountId: lead.convertedAccountId || null, opportunityId: lead.convertedOpportunityId || null, isReplay: true };
    if (lead.status !== 'qualified') throw new ApiError('ابتدا سرنخ را تأیید کنید.', 409);
    if (!request.createContact && !request.contactId && !request.createAccount && !request.accountId && !request.createOpportunity) throw new ApiError('حداقل یک خروجی برای تبدیل انتخاب کنید.', 400);
    if ((request.amount ?? 0) < 0) throw new ApiError('مبلغ نمی‌تواند منفی باشد.', 400);

    const state = moduleMockStore.get();
    const now = new Date().toISOString();
    let contactId = request.contactId || null;
    let accountId = request.accountId || null;
    let opportunityId: string | null = null;
    if (contactId && !state.contacts.some((contact) => contact.id === contactId)) throw new ApiError('مخاطب انتخاب‌شده پیدا نشد.', 404);
    if (accountId && !state.accounts.some((account) => account.id === accountId)) throw new ApiError('سازمان انتخاب‌شده پیدا نشد.', 404);
    if (request.createContact) {
      const phone = normalizePhone(lead.mobile || lead.chiefExecutivePhone || lead.contactPersonPhone);
      const email = normalizeEmail(lead.email);
      const duplicate = state.contacts.find((contact) => contact.contactPoints.some((point) => point.type === 'mobile' ? Boolean(phone) && normalizePhone(point.value) === phone : Boolean(email) && normalizeEmail(point.value) === email));
      if (duplicate) throw new ApiError(`مخاطب مشابه وجود دارد؛ مخاطب «${duplicate.id}» را متصل کنید.`, 409);
      contactId = createUuid();
      state.contacts.unshift({ id: contactId, firstName: lead.firstName || lead.chiefExecutiveName || lead.contactPersonName || null, lastName: lead.lastName, ownerMembershipId: lead.ownerMembershipId, status: 'active', contactPoints: [{ type: 'mobile' as const, value: lead.mobile || lead.chiefExecutivePhone || lead.contactPersonPhone }, { type: 'email' as const, value: lead.email }].filter((item): item is { type: 'mobile' | 'email'; value: string } => Boolean(item.value)).map((item) => ({ id: createUuid(), type: item.type, value: item.value, isPrimary: true, isVerified: false })), createdAtUtc: now, updatedAtUtc: null, rowVersion: mockDatabase.nextRowVersion() });
    }
    if (request.createAccount) {
      if (!lead.companyName) throw new ApiError('برای ایجاد سازمان، نام شرکت سرنخ الزامی است.', 400);
      const duplicate = state.accounts.find((account) => account.name.trim().toLowerCase() === lead.companyName?.trim().toLowerCase());
      if (duplicate) throw new ApiError(`سازمان مشابه وجود دارد؛ سازمان «${duplicate.id}» را متصل کنید.`, 409);
      accountId = createUuid();
      state.accounts.unshift({ id: accountId, name: lead.companyName, industry: lead.industry || null, organizationIdentifier: null, parentAccountId: null, ownerMembershipId: lead.ownerMembershipId, createdAtUtc: now, updatedAtUtc: null, rowVersion: mockDatabase.nextRowVersion() });
    }
    if (request.createOpportunity) {
      opportunityId = createUuid();
      state.opportunities.unshift({ id: opportunityId, name: request.opportunityName || lead.companyName || 'فرصت جدید', amount: request.amount || 0, currency: request.currency || 'IRR', ownerMembershipId: lead.ownerMembershipId || getUser(accessToken).membershipId, pipelineId: '90000000-0000-4000-8000-000000000001', stageId: '90000000-0000-4000-8000-000000000002', contactId, accountId, sourceLeadId: leadId, status: 'open', expectedCloseAtUtc: null, createdAtUtc: now, updatedAtUtc: null, rowVersion: mockDatabase.nextRowVersion() });
    }
    const updated: Lead = { ...lead, status: 'converted', convertedContactId: contactId, convertedAccountId: accountId, convertedOpportunityId: opportunityId, updatedAtUtc: now, rowVersion: mockDatabase.nextRowVersion() };
    leads[index] = updated;
    mockDatabase.saveLeads(leads);
    state.leadHistory[leadId] = [{ id: createUuid(), fromStatus: 'qualified', toStatus: 'converted', actorMembershipId: getUser(accessToken).membershipId, reason: 'تبدیل سرنخ', occurredAtUtc: now }, ...(state.leadHistory[leadId] || [])];
    moduleMockStore.save(state);
    return { leadId, contactId, accountId, opportunityId, isReplay: false };
  },
};

export const demoDirectory = [
  {
    membershipId: demoUsers.employee.membershipId,
    displayName: demoUsers.employee.displayName,
    teamId: demoTeam.id,
    teamName: demoTeam.name,
  },
  {
    membershipId: demoUsers.manager.membershipId,
    displayName: demoUsers.manager.displayName,
    teamId: demoTeam.id,
    teamName: demoTeam.name,
  },
];
